<template>
  <div class="flex flex-col items-center justify-center py-12">
    <!-- Progress Bar Container -->
    <div class="w-full max-w-md mb-4">
      <div class="relative">
        <!-- Background track -->
        <div class="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <!-- Animated progress bar -->
          <div 
            class="h-full bg-gradient-to-r from-[#157813] to-[#46cc44] rounded-full transition-all duration-300 ease-out relative overflow-hidden"
            :style="{ width: `${progress}%` }"
          >
            <!-- Shimmer effect -->
            <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent shimmer"></div>
          </div>
        </div>
        
        <!-- Progress percentage -->
        <div class="text-center mt-2">
          <span class="text-sm font-medium text-[#157813]">{{ Math.round(progress) }}%</span>
        </div>
      </div>
    </div>

    <!-- Spinning icon -->
    <div class="relative">
      <div class="animate-spin rounded-full h-8 w-8 border-2 border-[#157813] border-t-transparent"></div>
      <div class="absolute inset-0 rounded-full h-8 w-8 border border-[#157813]/20"></div>
    </div>

    <!-- Loading text -->
    <div class="mt-4 text-center">
      <span class="text-lg font-medium text-gray-900">{{ title }}</span>
      <p v-if="subtitle" class="text-sm text-gray-600 mt-1">{{ subtitle }}</p>
    </div>

    <!-- Additional status text -->
    <div v-if="status" class="mt-2 text-center">
      <span class="text-xs text-gray-500 px-3 py-1 bg-gray-100 rounded-full">
        {{ status }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';

interface Props {
  title?: string;
  subtitle?: string;
  status?: string;
  duration?: number; // Duration in milliseconds for progress animation
  autoProgress?: boolean; // Whether to auto-animate progress
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Loading...',
  subtitle: '',
  status: '',
  duration: 3000,
  autoProgress: true
});

const progress = ref(0);
let progressInterval: number | undefined;

const startProgress = () => {
  if (!props.autoProgress) return;
  
  progress.value = 0;
  const incrementInterval = 50; // Update every 50ms
  const incrementAmount = (100 / props.duration) * incrementInterval;
  
  progressInterval = window.setInterval(() => {
    if (progress.value < 95) {
      // Slow down as we approach 95%
      const slowdownFactor = progress.value > 80 ? 0.3 : 1;
      progress.value += incrementAmount * slowdownFactor;
    }
  }, incrementInterval);
};

const setProgress = (value: number) => {
  progress.value = Math.max(0, Math.min(100, value));
};

const completeProgress = () => {
  progress.value = 100;
  if (progressInterval) {
    clearInterval(progressInterval);
  }
};

// Expose methods for parent components
defineExpose({
  setProgress,
  completeProgress
});

onMounted(() => {
  startProgress();
});

onUnmounted(() => {
  if (progressInterval) {
    clearInterval(progressInterval);
  }
});
</script>

<style scoped>
/* Shimmer animation */
@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.shimmer {
  animation: shimmer 2s infinite;
}

/* Pulse animation for the spinner */
@keyframes pulse-glow {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(21, 120, 19, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(21, 120, 19, 0);
  }
}

.animate-spin {
  animation: spin 1s linear infinite, pulse-glow 2s ease-in-out infinite;
}

/* Smooth gradient transition */
.bg-gradient-to-r {
  background-size: 200% 200%;
  animation: gradient-shift 3s ease-in-out infinite;
}

@keyframes gradient-shift {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}
</style>